import type { ProcessedDailyLog } from "./services/dailyLogProcessor";

export enum QuoteMode {
  SUB_TO_PRIME = "SUB_TO_PRIME",
  PRIME_FULL = "PRIME_FULL",
  SERVICE = "SERVICE",
}

export enum QuoteStatus {
  DRAFT = "DRAFT",
  SENT = "SENT",
  WON = "WON",
  LOST = "LOST",
}

export enum CustomerStatus {
  LEAD = "LEAD",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export enum InvoiceType {
  PROGRESS = "PROGRESS",
  FINAL = "FINAL",
}

export enum InvoiceStatus {
  DRAFT = "DRAFT",
  ISSUE_PENDING_SYNC = "ISSUE_PENDING_SYNC", // Number allocated, QBO sync in progress
  ISSUED = "ISSUED", // Fully issued and synced to QBO
  READY = "READY", // PDF generated, ready to send
  SENT = "SENT", // Email sent to customer
  VIEWED = "VIEWED", // Customer opened (optional tracking)
  PARTIAL = "PARTIAL", // Partially paid
  PAID = "PAID", // Fully paid
  OVERDUE = "OVERDUE", // Past due date
  VOID = "VOID", // Cancelled
  SUPERSEDED = "SUPERSEDED", // Replaced by a new invoice
}

export type SubcontractUnitType = "LUMP" | "HOUR" | "DAY";
export type AIActionType =
  | "CREATE_TASK"
  | "DRAFT_EMAIL"
  | "CREATE_FOLLOWUP"
  | "FLAG_RISK"
  | "GENERAL_ADVICE"
  | "ADD_CUSTOMER"
  | "CREATE_TIME_ENTRY"
  | "QUOTE_SENT"
  | "QUOTE_WON"
  | "INVOICE_CREATED"
  | "MATERIAL_ORDERED"
  | "CUSTOMER_CONTACTED";
export type AIProvider = "GEMINI" | "OPENAI" | "ANTHROPIC";
export type MaintenanceFrequency = "WEEKLY" | "MONTHLY" | "QUARTERLY" | "YEARLY";

// Contact types (Phase 1: AI-powered inbox management)
export type ContactType = "customer" | "vendor" | "prospect" | "unknown";
export type InteractionType =
  | "EMAIL_IN"
  | "EMAIL_OUT"
  | "PHONE"
  | "TEXT"
  | "DAILY_LOG"
  | "MEETING"
  | "SITE_VISIT";
export type SourceKind = "email" | "daily_log" | "manual";
export type SentimentTrend = "positive" | "neutral" | "negative";
export type FactType = "personal" | "business" | "preference";
export type FactReviewStatus = "pending" | "approved" | "rejected";

export interface Contact {
  id: string; // UUID/ULID
  orgId: string;
  customerId: string | null; // FK to customers table
  displayName: string;
  companyName: string | null;
  contactType: ContactType;
  storePersonalDetails: boolean; // Privacy control
  retentionMonths: number | null; // Auto-purge policy
  createdAt: number;
  updatedAt: number;
}

export interface ContactEmail {
  contactId: string;
  email: string; // Normalized: lowercase, trimmed
  orgId: string;
  isPrimary: boolean;
  createdAt: number;
}

export interface ContactInteraction {
  id: string; // UUID
  contactId: string;
  orgId: string;
  interactionType: InteractionType;
  interactionDate: number; // Unix timestamp
  sourceKind: SourceKind;
  sourceId: string; // email_id, daily_log_id, etc.
  summary: string | null;
  extractedContext: string | null; // JSON
  confidence: number | null; // 0-1
  createdAt: number;
  quoteId?: string | null; // Optional project association (quote.id)
}

export interface EmailAnalysisThreadEmail {
  date: string;
  subject: string;
  from: string;
  snippet: string;
}

export interface EmailAnalysisCustomerContext {
  id: string;
  companyName?: string;
  name?: string;
  contactName?: string;
  email?: string;
  phone?: string;
}

export interface EmailAnalysisContextResponse {
  contact: Contact | null;
  contacts: Contact[];
  contactEmails: Record<string, string[]>;
  recentInteractions: ContactInteraction[];
  threadEmails: EmailAnalysisThreadEmail[];
  businessContext: string[];
  projectContext: string[];
  project: { quoteId: string; projectName?: string; jobCode?: string } | null;
  customer?: EmailAnalysisCustomerContext | null;
}

export interface ContactContext {
  contactId: string;
  orgId: string;
  relationshipSummary: string | null;
  lastInteractionAt: number | null;
  lastInteractionType: string | null;
  interactionCount: number;
  sentimentTrend: SentimentTrend | null;
  personalDetails: string | null; // JSON array
  businessNotes: string | null; // JSON array
  communicationPreferences: string | null; // JSON array
  cacheGeneratedAt: number | null;
  cacheVersion: number;
}

export interface ContactFactPendingReview {
  id: string;
  contactId: string;
  orgId: string;
  factType: FactType;
  factValue: string;
  sourceInteractionId: string | null;
  confidence: number | null;
  status: FactReviewStatus;
  reviewedAt: number | null;
  createdAt: number;
}

export interface AISettings {
  provider: AIProvider;
  apiKey: string;
}

export interface CompanyInfo {
  name: string;
  address: string;
  email: string;
  phone: string;
  gstNumber?: string;
  valueProp?: string;
  targetAudience?: string; // New: Who are we targeting?
}

export interface LaborRole {
  id: string;
  name: string;
  rate: number;
  costRate?: number;
  cost?: number;
}

export interface LaborStep {
  id: string;
  description: string;
  roleId: string;
  manHours: number;
  hours?: number;
}

export interface MaterialLine {
  id: string;
  description: string;
  modelNumber?: string;
  vendor: string;
  unitCost: number;
  qty: number;
  quantity?: number;
  pstFactor: number;
  markupFactor: number;
  leadTimeWeeks?: number;
}

export interface ActualMaterialItem {
  id: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  vendor: string;
  description: string;
  qty: number;
  unitCost: number;
  totalCost: number;
  dateLogged: string;
}

export interface SubcontractLine {
  id: string;
  description: string;
  vendor: string;
  totalCost: number;
  qty: number;
  unitCost: number; // usually derived or input
  unitType?: SubcontractUnitType;
  chargesGst: boolean;
  markupFactor: number;
}

export interface BidItem {
  id: string;
  code: string;
  title: string;
  part: number; // 1=Supply, 2=Install, etc. (Custom logic)
  laborSteps: LaborStep[];
  laborLines?: LaborStep[];
  materialLines: MaterialLine[];
  subcontractLines: SubcontractLine[];
  actualMaterials?: ActualMaterialItem[];
  shiftHoursPerDay: number;
  crewSize: number;
  contingencyPercent: number;
  notes: string;
  outageGroupId?: string;
  enableLoa?: boolean; // Per-item LOA enable/disable
}

export interface OutageGroup {
  id: string;
  name: string;
  allowedDays: number;
}

export interface BidFormRow {
  id: string;
  label: string;
}

export interface BidFormTemplate {
  id: string;
  name: string;
  rows: BidFormRow[];
}

export interface ChecklistItem {
  id: string;
  category: string;
  label: string;
  checked: boolean;
}

export type PdfTemplate = "STANDARD" | "SIMPLE";
export type LaborDisplayMode = "DETAILED" | "GROUPED" | "LUMP_SUM";
export type MaterialDisplayMode = "DETAILED" | "LUMP_SUM";

export interface PdfExportSettings {
  laborDisplayMode?: LaborDisplayMode;
  materialDisplayMode?: MaterialDisplayMode;
  showScope?: boolean;
  template?: PdfTemplate;
  // Legacy fields for migration (deprecated)
  showLaborBreakdown?: boolean;
  showMaterialBreakdown?: boolean;
}

export interface QuoteHistory {
  version: number;
  date: string;
  status: QuoteStatus;
  totalAmount: number;
  snapshotNote?: string;
}

/**
 * Milestone type for quick-add templates (P2.3)
 */
export type MilestoneType =
  | "mobilize"
  | "install_complete"
  | "inspection"
  | "commissioning"
  | "handover"
  | "custom";

/**
 * Source of milestone creation (P2.3)
 */
export type MilestoneSource = "manual" | "phase_auto";

/**
 * Schedule milestone for tracking project progress (P2.3)
 *
 * Milestones represent key dates in the project timeline:
 * - Mobilization, installation complete, inspections, commissioning, handover
 * - Can be manually created or auto-generated from phases
 * - Optional link to a phase via phaseId
 */
export interface ScheduleMilestone {
  /** Unique identifier for stable editing (required for new milestones, generated for legacy) */
  id: string;
  /** Display name (e.g., "Mobilize", "Install Complete", "Inspection: Phase 1") */
  name: string;
  /** Target date in YYYY-MM-DD format (local) */
  date: string;
  /** Whether this milestone has been completed */
  completed: boolean;
  /** Template type for categorization and icons */
  type?: MilestoneType;
  /** Optional link to a SchedulePhase.id if auto-generated */
  phaseId?: string;
  /** Optional notes */
  notes?: string;
  /** How this milestone was created */
  source?: MilestoneSource;
}

/**
 * Schedule phase for phased/multi-visit project scheduling (P2.1)
 *
 * When phases are present, the project is in "phased mode":
 * - Each phase contains a subset of bid items
 * - Phases can have dependencies on other phases
 * - Phases can require inspection windows between them
 * - Each phase can optionally have its own Outlook event
 */
export interface SchedulePhase {
  /** Unique identifier for this phase */
  id: string;
  /** Display name (e.g., "Phase 1: Prep", "Phase 2: Install") */
  name: string;
  /** IDs of bid items included in this phase */
  bidItemIds: string[];
  /** Computed or manually set start date (YYYY-MM-DD local) */
  startDate?: string;
  /** Computed or manually set end date (YYYY-MM-DD local) */
  endDate?: string;
  /** Working days buffer before this phase starts */
  bufferBeforeDays: number;
  /** Working days buffer after this phase ends (e.g., cleanup time) */
  bufferAfterDays: number;
  /** IDs of phases that must complete before this phase can start */
  dependsOn?: string[];
  /** Whether this phase requires an inspection before the next phase */
  requiresInspection?: boolean;
  /** Working days to wait for inspection after this phase completes */
  inspectionWindowDays?: number;
  /** Phase-specific notes */
  notes?: string;
  /** Outlook event ID if this phase is synced individually (P2.4) */
  outlookEventId?: string;
  /** Sync status for this phase's Outlook event (P2.4) */
  syncStatus?: ScheduleSyncStatus;
  /** Timestamp when this phase was last synced to Outlook (P2.4) */
  lastSyncedAt?: number;
  /** Timestamp when drift was last checked for this phase (P2.4) */
  lastDriftCheckAt?: number;
  /** Last sync error message for this phase (P2.4) */
  lastSyncError?: string;
  /** Last known Outlook event state for drift detection (P2.4) */
  outlookLastKnown?: {
    start: string;
    end: string;
    updatedAt: number;
  };
  /** Order index for display (0-based) */
  order: number;
}

/**
 * Phase scheduling status for "What needs scheduled now?" logic
 */
export type PhaseSchedulingStatus =
  | "unscheduled" // No dates assigned yet
  | "ready" // Dependencies met, materials ready, can be scheduled
  | "blocked_deps" // Waiting for dependent phases to complete
  | "blocked_materials" // Waiting for material lead times
  | "scheduled" // Has start/end dates
  | "in_progress" // Current date is within phase window
  | "completed"; // Phase end date is in the past

/**
 * Sync status for schedule ↔ Outlook calendar integration
 */
export type ScheduleSyncStatus = "not_synced" | "synced" | "failed" | "drift";

/**
 * Phase Allocations (Schedule Phasing)
 *
 * Represents a partial allocation of a bid item's labor hours into a specific schedule phase.
 * Stored separately from the Quote to avoid duplicating bid items or modifying quote totals.
 */
export interface PhaseBidItemAllocation {
  id: string;
  projectId: string;
  phaseId: string;
  bidItemId: string;
  /** Allocated labor hours for this bid item within this phase (stored to 2 decimals). */
  allocatedHours: number;
  /** Optional label to describe the allocation (e.g., "Remote", "On-site"). */
  allocationLabel?: string;
  /** Optional stable ordering within a phase/bid item grouping. */
  sortOrder?: number;
  createdAt: number;
  updatedAt: number;
}

export interface ProjectSchedule {
  startDate: string;
  endDate: string;
  adminBufferDays: number;
  materialLeadTimeWeeks: number;
  crewSizeUsed: number;
  notes?: string;
  milestones?: ScheduleMilestone[];

  // Outlook calendar sync (P0.2)
  outlookEventId?: string;
  syncStatus?: ScheduleSyncStatus;
  lastSyncedAt?: number;
  lastDriftCheckAt?: number;
  lastSyncError?: string;

  // Last known Outlook event state (for drift detection)
  outlookLastKnown?: {
    start: string;
    end: string;
    updatedAt: number;
  };

  // Outlook sync mode (P2.4)
  // Determines how phased schedules are synced to Outlook:
  // - 'single': One event for the entire schedule (default)
  // - 'per_phase': One event per phase (multi-visit projects)
  syncMode?: "single" | "per_phase";

  // Phased scheduling (P2.1)
  // If phases is present and non-empty, schedule is in "phased mode"
  // If absent or empty, schedule is in "single-block mode" (default)
  phases?: SchedulePhase[];

  // P3.1: Drag-and-drop support
  // Recorded when a schedule is confirmed (derived from labor + crew assumptions)
  // Enables recomputing end dates reliably during drag/drop without re-deriving from dates/holidays
  workDaysDuration?: number;
}

export interface ProposalSettings {
  tone: "PROFESSIONAL" | "PERSUASIVE" | "URGENT";
  length: "CONCISE" | "STANDARD" | "DETAILED";
  focus: "VALUE" | "SCHEDULE" | "PRICE" | "SAFETY";
  includeSections: string[];
  customInstructions?: string; // New: RFP Compliance Text
}

/**
 * Company-wide scheduling settings (P1.3)
 * Defines default values for schedule calculations
 */
export interface SchedulingSettings {
  /** Working days of the week (0=Sunday, 1=Monday, ... 6=Saturday) */
  workingDays: number[];
  /** Working hours start time (e.g., "08:00") */
  workingHoursStart: string;
  /** Working hours end time (e.g., "16:00") */
  workingHoursEnd: string;
  /** List of holidays in YYYY-MM-DD format */
  holidays: string[];
  /** Default crew size for schedule calculations */
  defaultCrewSize: number;
  /** Default shift length in hours */
  defaultShiftHours: number;
  /** Default admin/mobilization buffer in working days */
  defaultAdminBufferDays: number;
}

// --- MAINTENANCE TYPES ---
export interface MaintenanceChecklist {
  id: string;
  name: string; // e.g. "Monthly Generator Check"
  frequency: MaintenanceFrequency;
  sections?: { id: string; label: string }[];
  items: {
    id: string;
    label: string;
    sectionId?: string;
    fields?: MaintenanceChecklistField[];
    comparison?: MaintenanceChecklistComparison;
  }[];
}

export type MaintenanceChecklistFieldType = "TEXT" | "NUMBER";

export interface MaintenanceChecklistField {
  key: string;
  label: string;
  unit?: string;
  inputType?: MaintenanceChecklistFieldType;
  placeholder?: string;
}

export interface MaintenanceChecklistComparison {
  label: string;
  fieldKeys: [string, string];
  unit?: string;
}

export interface ServiceReportItem {
  checklistId: string;
  itemId: string;
  label: string;
  status: "PASS" | "FAIL" | "NA";
  notes?: string;
  fixAppliedStatus?: "NONE" | "FIXED_ON_SITE" | "NOT_FIXED";
  fixAppliedNotes?: string;
  recommendedFix?: string;
  fieldValues?: Record<string, string>;
}

export interface ServiceReport {
  id: string;
  date: string;
  /**
   * Canonical local date for when the visit occurred (YYYY-MM-DD, local time).
   * Optional for backward compatibility with existing saved reports.
   */
  visitDate?: string;
  /**
   * Optional multi-day window for a visit (default 0 = single day).
   * Kept optional to avoid breaking existing data.
   */
  visitWindowDays?: number;
  /**
   * Billing/service period anchor (YYYY-MM) used by anchored cadences.
   * Optional for backward compatibility; derived when saving new reports.
   */
  servicePeriod?: string;
  technician: string;
  /**
   * Person name of the technician (not company name).
   * Optional for backward compatibility with existing reports.
   */
  technicianName?: string;
  /**
   * Optional technician identifier (e.g., employee ID).
   */
  technicianId?: string;
  summary: string;
  checklistResults: ServiceReportItem[];
  issuesFound: string[];
  recommendations: string[];
  fixesApplied?: string[];
  recommendedFixes?: string[];
  invoiced: boolean;
  invoiceId?: string;
  /**
   * Explicit completion flag for scheduling. Optional for backward compatibility:
   * - If no reports contain a `completed` field, legacy behavior treats all reports as completed.
   * - If any report contains `completed`, scheduling should only consider `completed === true`.
   */
  completed?: boolean;
}

export interface MaintenanceCadenceAnchor {
  type: "MONTHLY_DAY" | "WEEKLY_DAY" | "QUARTERLY_DAY" | "YEARLY_DATE";
  day?: number; // 1-31 for monthly, 0-6 for weekly, etc.
  month?: number; // 1-12 for yearly anchors
}

export interface MaintenanceCadenceConfig {
  mode: "RELATIVE" | "ANCHOR";
  anchor?: MaintenanceCadenceAnchor;
  timezone?: string;
  monthShortRule?: "LAST_DAY" | "SKIP";
}

export interface MaintenanceSettings {
  cadenceConfig?: Record<string, MaintenanceCadenceConfig>;
  serviceSiteAddress?: string;
}

export interface MaintenanceNextDue {
  /** Computed next due date (local YYYY-MM-DD) */
  nextDueDate: string;
  /** Stable idempotency key: `${quoteId}:${checklistId}` */
  maintenanceKey: string;
  /** ISO timestamp of when we last scheduled/updated the calendar event */
  lastScheduledAt?: string;
  /** Outlook master event ID for recurring series (anchored monthly/weekly) */
  outlookMasterEventId?: string;
  /** Outlook event ID for single next-event scheduling (relative/ad-hoc) */
  outlookEventId?: string;
  /** Optional: iCalUId for recovery/cross-client stable identifier */
  iCalUId?: string;
  /** Optional: link to open the event in Outlook */
  webLink?: string;
}

export interface Quote {
  id: string;
  clientName: string;
  projectName: string;
  customerId?: string;
  qboJobId?: string; // QuickBooks Job (sub-customer) created per project
  primeContractor?: string;
  mode: QuoteMode;
  status: QuoteStatus;
  createdAt?: string;
  createdDate?: string;
  dueDate: string;
  gstRate: number;
  version: number;
  revisionHistory: QuoteHistory[];
  laborRoles: LaborRole[];
  defaultMarkup: number;
  defaultPst: number;
  pdfSettings: PdfExportSettings;
  documentType?: "QUOTE" | "ESTIMATE";
  showOutageAnalysis: boolean;
  enableLoa?: boolean;
  loaRate?: number;
  outageGroups?: OutageGroup[];
  bidItems: BidItem[];
  selectedTemplateId?: string;
  bidFormMappings?: Record<string, string[]>;
  checklist: ChecklistItem[];
  customBidFormRows?: BidFormRow[];
  schedule?: ProjectSchedule;
  jobCode?: string;
  sentDate?: string;
  wonDate?: string;
  completedDate?: string;
  winningBidAmount?: number;
  competitorName?: string;
  lossReason?: string;
  responseToken?: string;
  terms?: string;
  coverLetter?: string;
  scopeOfWork?: string;
  proposalSettings?: ProposalSettings;
  enableBidForm?: boolean;
  unassignedMaterials?: MaterialLine[];
  unassignedInvoiceItems?: ActualMaterialItem[];
  showVendorColumn?: boolean;
  isChangeOrder?: boolean;
  parentQuoteId?: string;
  changeOrderNumber?: number;
  totalPrice?: number;
  totalManHours?: number;
  subcontractorCost?: number;
  // Maintenance specific
  maintenanceChecklists?: MaintenanceChecklist[];
  serviceReports?: ServiceReport[];
  maintenanceSettings?: MaintenanceSettings;
  maintenanceNextDue?: Record<string, MaintenanceNextDue>;
  // AI & Default Settings
  aiScopeStyle?: "BRIEF" | "BULLET" | "DETAILED";
  defaultContingencyPercent?: number;
  defaultOutageGroupId?: string;
  // OneDrive storage link
  oneDrive?: {
    driveItemId: string;
    webUrl: string;
    downloadUrl?: string;
    uploadedAt: string;
  };
}

export interface InvoiceLineItem {
  id: string;
  bidItemId?: string;
  laborStepId?: string;
  materialLineId?: string;
  description: string;
  amount: number;
  category?: "LABOR" | "MATERIALS" | "SUBCONTRACTOR" | "OTHER";
}

// Invoice PDF generation options
export interface InvoicePdfOptions {
  detailLevel: "SUMMARY" | "CATEGORIES" | "LINE_ITEMS" | "FULL_DETAIL";
  showLaborBreakdown: boolean;
  showMaterialBreakdown: boolean;
  showHourlyRates: boolean;
  showMarkups: boolean;
  groupByBidItem: boolean;
  includePhotos: boolean;
  includeTerms: boolean;
}

// Invoice reminder for payment follow-up
export interface InvoiceReminder {
  id: string;
  scheduledFor: string;
  sentAt?: string;
  type: "UPCOMING" | "DUE" | "OVERDUE";
  method: "EMAIL" | "SMS";
  status: "SCHEDULED" | "SENT" | "CANCELLED";
}

// OneDrive storage link
export interface InvoiceOneDriveLink {
  driveItemId: string;
  webUrl: string;
  downloadUrl?: string;
  shareLink?: string;
  uploadedAt: string;
}

// Outlook email link
export interface InvoiceOutlookLink {
  messageId: string;
  conversationId?: string;
  sentTo: string[];
  ccTo?: string[];
  sentAt: string;
}

// QuickBooks sync link
export interface InvoiceQBOLink {
  invoiceId: string;
  syncToken: string;
  syncedAt: string;
  status?: string;
  docNumber?: string;
  totalAmt?: number;
  taxTotal?: number;
  balance?: number;
  txnDate?: string;
  dueDate?: string;
  lastError?: string; // Last QBO sync error message for diagnostics
  lastErrorAt?: string; // ISO timestamp when last error occurred
  balanceRefreshedAt?: string; // ISO timestamp when balance was last refreshed from QBO
  // Estimated totals (captured at issue time for comparison with QBO actuals)
  estimatedTaxTotal?: number;
  estimatedTotal?: number;
  estimatedAt?: string; // ISO timestamp when estimates were captured
}

// Invoice intent - declares what kind of billing milestone this is
export type InvoiceIntent = "DEPOSIT" | "PROGRESS" | "FINAL";

export interface Invoice {
  id: string;
  quoteId: string;
  number: string; // Legacy field, kept for backward compatibility

  // Type & Status
  type: InvoiceType;
  status: InvoiceStatus;

  // Intent - what kind of billing milestone is this?
  invoiceIntent?: InvoiceIntent;

  // Invoice numbering (new hybrid model)
  invoiceNumber?: string; // Nullable for drafts (e.g., "INV-000001")
  invoiceSeq?: number; // Global sequence number
  issuedAt?: string; // ISO timestamp when issued
  issueDate?: string; // Legacy alias
  createdAt?: string;
  docType?: "INVOICE" | "CHANGE_ORDER" | "CREDIT_MEMO"; // Document type
  baseInvoiceId?: string; // For CO/CM: links to base invoice
  changeOrderCount?: number; // For base invoices: count of change orders
  supersedesInvoiceId?: string; // For reissued invoices: original invoice ID
  idempotencyKey?: string; // For QBO sync idempotency

  // Amounts (backward compatible - 'amount' still works)
  amount: number; // Legacy: pre-tax total (kept for compatibility)
  totalAmount?: number;
  subtotal?: number; // Pre-tax subtotal
  taxRate?: number; // GST rate used (e.g., 0.05)
  taxAmount?: number; // Calculated tax
  total?: number; // subtotal + taxAmount
  amountPaid?: number; // From payments
  balanceDue?: number; // total - amountPaid

  // Category breakdowns
  laborTotal?: number;
  materialsTotal?: number;
  subsTotal?: number;

  // Line items
  lines: InvoiceLineItem[];

  // Dates
  date: string; // Invoice date
  dueDate?: string; // Payment due date
  sentDate?: string; // When email was sent
  paidDate?: string; // When fully paid

  // Content
  notes: string;
  customMessage?: string;
  terms?: string;

  // PDF options
  pdfOptions?: InvoicePdfOptions;
  pdfMeta?: {
    hash: string;
    cacheKey: string;
    lastGeneratedAt: string;
  };

  // Integration links
  oneDrive?: InvoiceOneDriveLink;
  outlook?: InvoiceOutlookLink;
  qbo?: InvoiceQBOLink;

  // Reminders
  reminders?: InvoiceReminder[];

  // Billing basis
  billingBasis?: "QUOTED" | "ITEMIZED" | "ACTUALS";

  // Project reference (denormalized for display)
  projectName?: string;
  // Authoritative link for recipient resolution; prefer over customerName/clientName.
  customerId?: string; // Stable customer ID for recipient resolution (Phase 2.1)
  customerName?: string;
}

export interface TimeEntry {
  id: string;
  category: "PROJECT" | "INTERNAL";
  quoteId?: string;
  itemId?: string;
  generalLabel?: string;
  roleId?: string;
  date: string;
  hours: number;
  description?: string;
  notes?: string;
  timestamp: number;
}

export interface Kit {
  id: string;
  name: string;
  description: string;
  category: string;
  laborSteps: LaborStep[];
  materialLines: MaterialLine[];
}

export interface PriceListItem {
  id?: string;
  modelNumber: string;
  description: string;
  vendor: string;
  unitCost: number;
  updatedAt: string;
  name?: string;
}

export interface BillingAddress {
  line1: string;
  line2?: string;
  city?: string;
  region?: string; // Province/State
  postalCode?: string;
  country?: string;
}

export interface Customer {
  id: string;
  companyName: string;
  name?: string;
  contactName: string;
  email: string;
  phone: string;

  /**
   * Legacy display field (freeform). Kept for backwards compatibility.
   * Prefer `billingAddress` going forward.
   */
  address: string;

  /** Structured billing address (sync-friendly, maps cleanly to QBO BillAddr). */
  billingAddress?: BillingAddress;

  category: string;
  status: CustomerStatus;
  notes: string;
  type?: string;
  industry?: string;
  leadScore?: LeadScore;
}

export interface SubcontractorRate {
  id: string;
  role: string;
  rate: number;
  unit: SubcontractUnitType;
}

export interface Subcontractor {
  id: string;
  companyName: string;
  name?: string;
  contactName: string;
  email: string;
  phone: string;
  defaultMarkup: number;
  notes: string;
  rates?: SubcontractorRate[];
}

export interface TermsTemplate {
  id: string;
  name: string;
  text: string;
}

export interface CalculationResult {
  totalManHours: number;
  totalLaborCost: number;
  totalLaborInternalCost: number;
  calculatedDays: number;
  totalMaterialSell: number;
  totalMaterialCost: number;
  totalSubsSell: number;
  totalSubsCost: number;
  basePreGst: number;
  contingencyAmount: number;
  totalPreGst: number;
  grossProfit: number;
  grossMarginPercent: number;
}

export interface AIBrief {
  date: string;
  topPriorities: string[];
  upcomingDeadlines: { title: string; date: string; daysLeft: number }[];
  risks: { severity: "HIGH" | "MEDIUM" | "LOW"; message: string }[];
  suggestedSchedule: { time: string; task: string }[];
  quickWins: string[];
  followUps: { contact: string; context: string; draftMessage: string }[];
  healthNudges: string[];
  _aiModel?: { provider: string; model: string; tier: string };
}

export interface AIActionProposal {
  id: string;
  type: AIActionType;
  title: string;
  description: string;
  data?: any;
  status: "PENDING" | "APPROVED" | "REJECTED";
  reasoning?: string;
}

// Re-export ProcessedDailyLog from dailyLogProcessor
export type { ProcessedDailyLog };

export interface ParsedDailyLog {
  date: string;
  location?: string;
  workCompleted: WorkItem[];
  projectsTouched: ProjectReference[];
  timeSummary?: {
    totalHours?: number;
    billableHours?: number;
    nonBillableHours?: number;
  };
  tools?: string[];
  issues?: string[];
  notes?: string[];
  followUps: FollowUpItem[];
  tomorrowPriorities?: string[];
  actions: LoggedAction[];
}

// Daily Log Data Structures
export type VoiceNoteTranscriptionStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"
  | "RETRYING";

export interface VoiceNoteAudioMetadata {
  mimeType: string;
  totalBytes: number;
  durationMs: number;
  startedAt: number;
  endedAt: number;
  chunkCount: number;
  wasSegmented: boolean;
}

export interface VoiceNote {
  id: string;
  timestamp: number;
  text: string;
  processed: boolean;
  processedAt?: number; // Timestamp when processed
  transcriptionStatus?: VoiceNoteTranscriptionStatus;
  transcriptionError?: string;
  retryCount?: number;
  audioMetadata?: VoiceNoteAudioMetadata;
  pendingCorrectionText?: string;
}

export interface ProcessedDailyLogState {
  lastProcessedAt: number;
  accumulatedTimeEntries: TimeEntry[];
  accumulatedActions: AIActionProposal[];
  allProcessedLogs: ProcessedDailyLog[];
  date: string; // YYYY-MM-DD
}

export interface DailyLogData {
  date: string; // YYYY-MM-DD
  voiceNotes: VoiceNote[];
  appActions: any[]; // AppActionLog[] - defined in dailyLogActionTracker.ts to avoid circular deps
  processedResults?: ProcessedDailyLogState;
  eodFinalization?: {
    finalizedAt?: number;
    pdfUploadedAt?: number;
    clearedAt?: number;
    lastAttemptAt?: number;
    lastError?: string;
    lastProcessor?: "cron" | "manual";
    uploadFileName?: string;
    uploadDriveItemId?: string;
  };
  lastProcessedAt?: number;
  lastSyncedAt: number;
}

export interface WorkItem {
  description: string;
  hours?: number;
  projectName?: string;
  role?: string;
  timeRange?: string; // "8:00 a.m." or "8am-5pm"
}

export interface ProjectReference {
  name: string;
  type?: string;
}

export interface FollowUpItem {
  description: string;
  dueDate?: string;
  priority?: "HIGH" | "MEDIUM" | "LOW";
  linkedProject?: string;
}

export interface LoggedAction {
  type:
    | "QUOTE_SENT"
    | "QUOTE_WON"
    | "INVOICE_CREATED"
    | "MATERIAL_ORDERED"
    | "CUSTOMER_CONTACTED"
    | "QUOTE_FINALIZED";
  entityName?: string; // Quote name, customer name, etc.
  details: Record<string, any>;
  requiresConfirmation: boolean;
}

export interface AIChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  _aiModel?: { provider: string; model: string; tier: string };
}

export interface SocialMediaDraft {
  platform: "LINKEDIN" | "FACEBOOK" | "INSTAGRAM";
  content: string;
  hashtags: string[];
  imageSuggestion: string;
}

export interface RFQDraft {
  vendorName: string;
  emailSubject: string;
  emailBody: string;
  itemCount: number;
}

export interface OutreachDraft {
  recipientName: string;
  method: "EMAIL" | "LINKEDIN_DM" | "PHONE_SCRIPT";
  subject?: string;
  content: string;
}

export interface LeadScore {
  score: number; // 0-100
  reasoning: string;
  lastUpdated: string;
}

export interface CaseStudy {
  title: string;
  challenge: string;
  solution: string;
  outcome: string;
  technicalHighlights: string[];
}

export interface ProjectFinancialHealth {
  projectId: string;
  projectName: string;
  budgetHours: number;
  actualHours: number;
  burnRate: number; // 0 to 1+
  status: "HEALTHY" | "WARNING" | "CRITICAL";
  aiAdvice: string;
}
